import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { useAuth } from '@/contexts/AuthContext'
import { useLoginAlert } from '@/contexts/LoginAlertContext'
import { useClickOutside } from '@/hooks/useClickOutside'
import { usePurchases } from '@/hooks/usePurchases'
import { routes } from '@/routes'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MenuIcon,
} from '@/components/ui/icons'
import { AddPurchaseForm } from './AddPurchaseForm'
import { PROFILE_NAV_ITEMS } from './profileNavItems'

/**
 * Menu do canto direito do cabeçalho.
 *
 * O conjunto de itens muda com a sessão: visitante vê "Entrar ou criar conta",
 * logado vê perfil, compras e sair. Até `md` ele também absorve os dois botões
 * que saem da barra — daí o ícone virar sanduíche nessa faixa.
 */
export function UserMenu() {
  const [open, setOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { isAuthenticated, isAdmin, logout } = useAuth()
  const { show: showLoginAlert } = useLoginAlert()
  const { purchases } = usePurchases()

  /** Fecha tudo: nada expandido pode reaparecer sozinho na próxima abertura. */
  function close() {
    setOpen(false)
    setAddOpen(false)
    setProfileOpen(false)
  }

  useClickOutside(containerRef, close, open)

  async function handleLogout() {
    close()
    await logout()
    navigate(routes.home)
  }

  function handleAddPurchase() {
    if (!isAuthenticated) {
      close()
      showLoginAlert()
      return
    }
    setAddOpen((current) => !current)
  }

  // Sem o `display`: os dois "Seu perfil" — o botão do celular e o link do
  // desktop — precisam decidir o seu por conta, e o `cn` daqui só concatena
  // (quem viesse depois não venceria por ordem).
  const itemBase =
    'w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm text-ink transition-colors hover:bg-surface-muted'
  const itemClass = cn('flex', itemBase)

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Menu da conta"
        className="flex size-8 items-center justify-center rounded text-white transition-opacity hover:opacity-80"
      >
        <MenuIcon className="size-6 md:hidden" />
        <ChevronDownIcon
          className={cn('hidden size-5 transition-transform md:block', open && 'rotate-180')}
        />
      </button>

      {open && (
        // `w-64` só no celular: é lá que o menu hospeda o campo de colar o
        // link, que ficaria apertado demais em 14rem.
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+0.9rem)] z-50 w-64 overflow-hidden rounded bg-surface py-1 shadow-pop animate-[slide-down_0.18s_ease-out] md:w-56"
        >
          {/* A conta vem sempre primeiro: é o item que orienta o resto do menu. */}
          {isAuthenticated ? (
            <>
              {/* No celular "Seu perfil" abre as abas em vez de navegar — elas
                  só aparecem depois de apertar. No desktop o menu lateral do
                  perfil já as mostra, então lá ele continua sendo um link. */}
              <button
                type="button"
                role="menuitem"
                onClick={() => setProfileOpen((current) => !current)}
                aria-expanded={profileOpen}
                className={cn(itemBase, 'flex md:hidden')}
              >
                Seu perfil
                <ChevronDownIcon
                  className={cn(
                    'size-4 text-brand transition-transform',
                    profileOpen && 'rotate-180',
                  )}
                />
              </button>

              <Link
                to={routes.profile}
                role="menuitem"
                className={cn(itemBase, 'hidden md:flex')}
                onClick={close}
              >
                Seu perfil
                <ChevronRightIcon className="size-4 text-brand" />
              </Link>

              {profileOpen && (
                // As abas do perfil, que no celular ficam só como ícones lá
                // dentro: é aqui que elas aparecem com nome.
                <div className="md:hidden">
                  {PROFILE_NAV_ITEMS.filter((item) => !item.desktopOnly).map(
                    ({ to, label, icon: Icon }) => (
                      <Link
                        key={to}
                        to={to}
                        role="menuitem"
                        onClick={close}
                        className="flex items-center gap-2.5 py-2.5 pl-9 pr-4 text-[13px] text-ink-soft transition-colors hover:bg-surface-muted hover:text-ink"
                      >
                        <Icon className="size-4 shrink-0" />
                        {label}
                      </Link>
                    ),
                  )}
                </div>
              )}
            </>
          ) : (
            <Link to={routes.login} role="menuitem" className={itemClass} onClick={close}>
              Entrar ou criar conta
            </Link>
          )}

          {/* Até `md` estes dois itens substituem os botões do cabeçalho. */}
          <div className="md:hidden">
            <button
              type="button"
              role="menuitem"
              onClick={handleAddPurchase}
              aria-expanded={addOpen}
              className={itemClass}
            >
              Adicionar compra
            </button>

            {addOpen && (
              <div className="border-y border-line bg-surface-muted p-2">
                <AddPurchaseForm autoFocus onDone={close} />
              </div>
            )}

            {!isAuthenticated && (
              <Link to={routes.shoppingList} role="menuitem" className={itemClass} onClick={close}>
                Já comprados
              </Link>
            )}
          </div>

          {isAuthenticated && (
            <>
              <Link
                to={routes.shoppingList}
                role="menuitem"
                className={itemClass}
                onClick={close}
              >
                Já comprados
                {purchases.length > 0 && (
                  <span className="flex min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[11px] font-bold leading-5 text-ink">
                    {purchases.length > 99 ? '99+' : purchases.length}
                  </span>
                )}
              </Link>

              {isAdmin && (
                <Link to={routes.admin} role="menuitem" className={itemClass} onClick={close}>
                  Produtos cadastrados
                </Link>
              )}

              <button type="button" role="menuitem" className={itemClass} onClick={handleLogout}>
                Sair
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
